/**
 * Shared working state of one map-generation run. The pipeline stages (see index.ts) read and
 * write these arrays in order; only index.ts turns them into the public, contract-shaped GameMap.
 */
import { HEX_W, HexGrid } from '../core/hex';
import { hashString, Rng } from '../core/rng';
import { isWater } from '../core/types';

export interface MapGenParams {
  seed: number;
  width: number;
  height: number;
  factionCount: number;
}

export interface MapContext {
  readonly seed: number;
  readonly factionCount: number;
  readonly grid: HexGrid;
  /** Hex count. */
  readonly n: number;
  /**
   * Hex centres in "hex units" (1 = distance between neighbouring centres). All noise is sampled
   * in this space so feature sizes are expressed in hexes, independent of HEX_SIZE.
   */
  readonly ux: Float64Array;
  readonly uy: Float64Array;
  /** Board extent in hex units. */
  readonly extentX: number;
  readonly extentY: number;
  /** Terrain per hex (only water / Plains until terrain classification). */
  readonly terrain: Uint8Array;
  /** Working elevation, 0..255 float (sea level 64). */
  readonly elevation: Float64Array;
  /** Working moisture, 0..1. */
  readonly moisture: Float64Array;
  /** River bitmask per hex (same encoding as GameMap.riverEdges). */
  readonly riverEdges: Uint8Array;
}

export function createContext(params: MapGenParams): MapContext {
  const grid = new HexGrid(params.width, params.height);
  const n = grid.count;
  const ux = new Float64Array(n);
  const uy = new Float64Array(n);
  for (let h = 0; h < n; h++) {
    ux[h] = grid.cx(h) / HEX_W;
    uy[h] = grid.cy(h) / HEX_W;
  }
  return {
    seed: params.seed >>> 0,
    factionCount: params.factionCount,
    grid,
    n,
    ux,
    uy,
    extentX: grid.worldWidth / HEX_W,
    extentY: grid.worldHeight / HEX_W,
    terrain: new Uint8Array(n),
    elevation: new Float64Array(n),
    moisture: new Float64Array(n),
    riverEdges: new Uint8Array(n),
  };
}

/**
 * Independent random stream per pipeline stage. Keying streams by stage name means tuning one
 * stage (drawing more or fewer numbers) never reshuffles the output of the others.
 */
export function stageRng(seed: number, stage: string): Rng {
  return new Rng((seed ^ hashString(stage)) >>> 0);
}

export const isLand = (t: number): boolean => !isWater(t);

/** All land hex ids in ascending order. */
export function landHexes(ctx: MapContext): number[] {
  const out: number[] = [];
  for (let h = 0; h < ctx.n; h++) if (isLand(ctx.terrain[h])) out.push(h);
  return out;
}

export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * The `k` candidates with the highest score (ties broken by lower id, so the result is stable).
 * Quota-based classification ("the 6 % highest land hexes become mountains") is how the generator
 * hits its terrain-mix targets exactly regardless of the noise's absolute range.
 */
export function topByScore(candidates: readonly number[], score: ArrayLike<number>, k: number): number[] {
  const sorted = candidates.slice().sort((a, b) => score[b] - score[a] || a - b);
  return sorted.slice(0, Math.max(0, Math.min(k, sorted.length)));
}
